/** Persistence-only Discovery import: deliberately has no generation dependencies. */
export interface DiscoveryImageSource { id: number; imageUrl: string; name: string; styleTags?: string[] | null;
  prompt?: string | null; wavespeedPrompt?: string | null; videoUrl?: string | null;
  adStyle?: string | null; adAngle?: string | null; conceptName?: string | null;
  headline?: string | null; bodyCopy?: string | null; ctaText?: string | null;
}
const probeImage = (src: string) => new Promise<{width:number;height:number}>((resolve, reject) => {
  const image = new Image();
  const timeout = setTimeout(() => reject(new Error('Source image took too long to load. Please try again.')), 15000);
  image.onload = () => { clearTimeout(timeout); resolve({width:image.naturalWidth || 1080,height:image.naturalHeight || 1080}); };
  image.onerror = () => { clearTimeout(timeout); reject(new Error('Could not load the source image. Please try again.')); };
  image.src = src;
});
export async function importDiscoveryImages(projectId: number, sources: DiscoveryImageSource[], request: typeof fetch = fetch, probe = probeImage): Promise<void> {
  const endpoint = `/api/canvas/projects/${projectId}/images`;
  const response = await request(endpoint);
  if (!response.ok) throw new Error('Could not open this canvas. Please try again.');
  const existing = await response.json();
  const saved = new Set<string>(existing.map((image: any) => image.src));
  const bottom = existing.reduce((max:number,image:any) => Math.max(max, Number(image.y || 0) + Number(image.displayHeight ?? image.display_height ?? 0)), 0);
  let x = 60;
  const y = bottom ? bottom + 80 : 60;
  for (const source of sources) {
    if (saved.has(source.imageUrl)) continue;
    const {width,height} = await probe(source.imageUrl);
    const scale = Math.min(300 / width, 400 / height, 1);
    const displayWidth = Math.round(width * scale), displayHeight = Math.round(height * scale);
    const result = await request(endpoint, {
      method:'POST', headers:{'Content-Type':'application/json'},
      body:JSON.stringify({clientId:crypto.randomUUID(),src:source.imageUrl,name:source.name,x,y,displayWidth,displayHeight,originalWidth:width,originalHeight:height,adStyle:source.adStyle ?? source.styleTags?.join(', '),prompt:source.prompt,wavespeedPrompt:source.wavespeedPrompt,videoUrl:source.videoUrl,adAngle:source.adAngle,conceptName:source.conceptName,headline:source.headline,bodyCopy:source.bodyCopy,ctaText:source.ctaText}),
    });
    if (!result.ok) throw new Error(`Could not save “${source.name}”. Already added images are saved; try again to add the rest.`);
    saved.add(source.imageUrl);
    x += displayWidth + 32;
  }
}
